import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Pill, Section } from '../components/ui-kit';
import { Shield } from 'lucide-react';

interface Threat {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  breach_name?: string;
  domain?: string;
  siren?: string;
  exposed_count?: number;
  breach_date?: string;
}
interface CyberStats { mfa_pct: number; failed_logins_24h: number; blocked_ips: number; active_threats: number; }

export default function Cyber() {
  const { getToken } = useXyqarAuth();
  const [stats, setStats] = React.useState<CyberStats | null>(null);
  const [threats, setThreats] = React.useState<Threat[]>([]);
  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const [s, th] = await Promise.all([
          apiGet<CyberStats>('/api/v1/admin/cyber/stats', t),
          apiGet<{ threats: Threat[] }>('/api/v1/admin/cyber/threats', t),
        ]);
        setStats(s); setThreats(th.threats ?? []);
      } catch { setStats({ mfa_pct: 0, failed_logins_24h: 0, blocked_ips: 0, active_threats: 0 }); }
    })();
  }, [getToken]);

  const criticalThreats = threats.filter((item) => item.severity === 'critical').length;
  const highThreats = threats.filter((item) => item.severity === 'high').length;
  const exposedTotal = threats.reduce((sum, item) => sum + (item.exposed_count ?? 0), 0);

  return (
    <div>
      <PageTitle kicker="Security operations" title="Cyber posture" />
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="MFA enrolled" value={`${stats.mfa_pct.toFixed(0)}%`} />
          <Stat label="Failed logins 24h" value={stats.failed_logins_24h} />
          <Stat label="Blocked IPs" value={stats.blocked_ips} />
          <Stat label="Active threats" value={stats.active_threats} highlight={stats.active_threats > 0} />
        </div>
      )}

      <Section title="Control room" hint="Authentication posture, threat intake, and exposure concentration are grouped here for quick triage.">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Identity hardening</p>
            <p className="mt-2 text-white text-sm">{stats ? `${stats.mfa_pct.toFixed(0)}% MFA coverage` : 'Awaiting stats payload'}</p>
            <p className="mt-1 text-xs text-white/50">Failed logins over 24h: {stats?.failed_logins_24h ?? 0}</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Threat intensity</p>
            <p className="mt-2 text-white text-sm">{criticalThreats} critical · {highThreats} high</p>
            <p className="mt-1 text-xs text-white/50">Blocked IPs currently visible: {stats?.blocked_ips ?? 0}</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Exposure concentration</p>
            <p className="mt-2 text-white text-sm">{exposedTotal.toLocaleString('fr-FR')} visible records exposed</p>
            <p className="mt-1 text-xs text-white/50">Only records surfaced by the current admin API are shown here.</p>
          </Card>
        </div>
      </Section>

      <Section title="Response lanes" hint="This page is meant to separate posture review from immediate incident handling.">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">1. Harden access</p>
            <p className="mt-2 text-sm text-white">Use low MFA coverage and failed-login spikes to decide whether identity controls need immediate intervention.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">2. Isolate tenants</p>
            <p className="mt-2 text-sm text-white">Threat rows identify the tenant domain or SIREN currently tied to the visible finding.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">3. Escalate with evidence</p>
            <p className="mt-2 text-sm text-white">This screen remains an evidence surface, not an incident-response console. Use it to qualify what needs deeper handling.</p>
          </Card>
        </div>
      </Section>

      <Section title="Security signals" hint="Current view combines authentication posture with breach records when the underlying evidence tables exist.">
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-4 w-4 text-amber-400" />
          <p className="text-sm text-white/80">Recent security signals</p>
        </div>
        {threats.length === 0 && <p className="text-white/40 text-xs">No high-severity breach findings surfaced by the current admin API.</p>}
        {threats.length > 0 && (
          <table className="w-full text-xs font-mono">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40"><tr><th className="text-left py-2">Date</th><th className="text-left py-2">Severity</th><th className="text-left py-2">Finding</th><th className="text-left py-2">Tenant</th><th className="text-left py-2">Exposure</th></tr></thead>
            <tbody>
              {threats.map((t) => (
                <tr key={t.id} className="border-t border-white/5">
                  <td className="py-2 text-white/60">{t.breach_date ? new Date(t.breach_date).toLocaleDateString() : '—'}</td>
                  <td className="py-2"><Pill kind={t.severity === 'critical' || t.severity === 'high' ? 'red' : t.severity === 'medium' ? 'amber' : 'gray'}>{t.severity}</Pill></td>
                  <td className="py-2 text-white">{t.breach_name ?? t.domain ?? 'finding'}</td>
                  <td className="py-2 text-white/60">{t.siren ?? t.domain ?? '—'}</td>
                  <td className="py-2 text-white/80">{typeof t.exposed_count === 'number' ? `${t.exposed_count.toLocaleString('fr-FR')} records` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      </Section>
    </div>
  );
}
function Stat({ label, value, highlight }: { label: string; value: number | string; highlight?: boolean }) {
  return <Card className={highlight ? 'border-red-400/30' : ''}><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</p><p className={`text-2xl mt-2 ${highlight ? 'text-red-400' : 'text-white'}`}>{value}</p></Card>;
}
